import { apiClient } from '@/services/api-client';
import {
  InvoicesResponse,
  InvoiceRecord,
  PayLinkResponse,
  PaymentMethodSummary,
  PlanEntitlements,
  PlanPrice,
  ProviderSubscriptionPreview,
  ProviderSubscriptionPreviewMeta,
  ProviderSubscriptionSummary,
  UsageMetric,
  UsageRecord,
  UsageRecordsResponse,
  UsageSummary,
} from '@/types/provider-customer-preview';
import { CustomerSubscriptionPlan, SubscriptionStatus, SubscriptionStatusColor } from '@/types/customer-subscriptions';
import {
  normalizeSubscriptionPlanOptionResource,
  SubscriptionPlanOption,
} from '@/services/provider/subscription-plan-options.service';

type RawRecord = Record<string, unknown>;

const readString = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const readNumber = (value: unknown): number | null => (typeof value === 'number' && !Number.isNaN(value) ? value : null);
const readBoolean = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : fallback;

function normalizePlan(plan: unknown): CustomerSubscriptionPlan | null {
  if (!plan || typeof plan !== 'object') {
    return null;
  }

  const planObj = plan as RawRecord;
  return {
    id: readString(planObj.id) ?? '',
    name: readString(planObj.name) ?? 'Unknown plan',
    trialDays: planObj.trial_days as number | null ?? (planObj.trialDays as number | null) ?? null,
  };
}

function normalizePrice(price: unknown): PlanPrice {
  if (!price || typeof price !== 'object') {
    return { unitAmount: null, currency: null, billingInterval: null };
  }
  const priceObj = price as RawRecord;
  return {
    unitAmount: (readNumber(priceObj.unit_amount) ?? readNumber(priceObj.unitAmount)),
    currency: readString(priceObj.currency),
    billingInterval: readString(priceObj.billing_interval ?? priceObj.billingInterval),
    introAmount:
      readNumber(priceObj.intro_amount) ??
      readNumber(priceObj.introAmount) ??
      readNumber(priceObj.intro_price) ??
      readNumber(priceObj.introPrice),
    recurringAmount:
      readNumber(priceObj.recurring_amount) ??
      readNumber(priceObj.recurringAmount) ??
      readNumber(priceObj.recurring_price) ??
      readNumber(priceObj.recurringPrice),
    introCycles: readNumber(priceObj.intro_cycles) ?? readNumber(priceObj.introCycles),
    introEndsAt: readString(priceObj.intro_ends_at ?? priceObj.introEndsAt),
    nextInvoiceAmount:
      readNumber(priceObj.next_invoice_amount) ??
      readNumber(priceObj.nextInvoiceAmount),
    nextInvoiceAt: readString(priceObj.next_invoice_at ?? priceObj.nextInvoiceAt),
  };
}

function normalizeEntitlements(entitlements: unknown): PlanEntitlements {
  if (!entitlements || typeof entitlements !== 'object') {
    return {};
  }

  const raw = entitlements as RawRecord;
  const seats = raw.seats && typeof raw.seats === 'object'
    ? {
        included: readNumber((raw.seats as RawRecord).included),
        used: readNumber((raw.seats as RawRecord).used),
      }
    : undefined;

  const projects = raw.projects && typeof raw.projects === 'object'
    ? {
        included: readNumber((raw.projects as RawRecord).included),
      }
    : undefined;

  const storage = raw.storage && typeof raw.storage === 'object'
    ? {
        includedGb: readNumber((raw.storage as RawRecord).included_gb ?? (raw.storage as RawRecord).includedGb),
        usedGb: readNumber((raw.storage as RawRecord).used_gb ?? (raw.storage as RawRecord).usedGb),
      }
    : undefined;

  const supportTier = readString(raw.support_tier ?? raw.supportTier);

  const entitlementsCopy: PlanEntitlements = { supportTier };
  if (seats) entitlementsCopy.seats = seats;
  if (projects) entitlementsCopy.projects = projects;
  if (storage) entitlementsCopy.storage = storage;

  return entitlementsCopy;
}

function normalizeSubscription(resource: RawRecord | undefined): ProviderSubscriptionSummary {
  if (!resource) {
    throw new Error('Subscription not found');
  }

  const attributes =
    resource.attributes && typeof resource.attributes === 'object'
      ? (resource.attributes as RawRecord)
      : resource;
  const periodRaw =
    attributes.period && typeof attributes.period === 'object'
      ? (attributes.period as RawRecord)
      : {};
  const flagsRaw =
    attributes.flags && typeof attributes.flags === 'object'
      ? (attributes.flags as RawRecord)
      : {};

  const status = (readString(attributes.status) as SubscriptionStatus | null) ?? 'active';

  return {
    id: readString(resource.id) ?? readString(attributes.id) ?? '',
    status,
    statusLabel: readString(attributes.status_label ?? attributes.statusLabel) ?? status ?? undefined,
    statusColor:
      (readString(attributes.status_color ?? attributes.statusColor) as SubscriptionStatusColor | null) ??
      'secondary',
    plan: normalizePlan(attributes.plan),
    price: normalizePrice(attributes.price),
    quantity: readNumber(attributes.quantity),
    period: {
      start: readString(
        attributes.current_period_start ?? periodRaw.start ?? attributes.period_start,
      ),
      end: readString(
        attributes.current_period_end ?? periodRaw.end ?? attributes.period_end,
      ),
    },
    trialEndsAt: readString(attributes.trial_ends_at ?? attributes.trialEndsAt),
    endsAt: readString(attributes.ends_at ?? attributes.endsAt),
    entitlements: normalizeEntitlements(attributes.entitlements),
    flags: {
      canChangePlan: readBoolean(
        flagsRaw.can_change_plan ?? flagsRaw.canChangePlan ?? attributes.can_change_plan,
      ),
      canSendPayLink: readBoolean(
        flagsRaw.can_send_pay_link ?? flagsRaw.canSendPayLink ?? attributes.can_send_pay_link,
      ),
    },
    links: (resource.links as { self?: string } | undefined) ?? undefined,
  };
}

function normalizeUsageMetric(rawMetric: RawRecord | undefined): UsageMetric {
  return {
    used: readNumber(rawMetric?.used) ?? 0,
    creditsUsed: readNumber(rawMetric?.credits_used ?? rawMetric?.creditsUsed),
    cost: readNumber(rawMetric?.cost),
    unit: readString(rawMetric?.unit) ?? '',
    limit: readNumber(rawMetric?.limit),
    percentUsed: readNumber(rawMetric?.percent_used ?? rawMetric?.percentUsed),
    status: (readString(rawMetric?.status) as UsageMetric['status']) ?? 'within',
  };
}

function normalizeUsageSummary(payload: RawRecord | undefined): UsageSummary {
  const data = payload?.data && typeof payload.data === 'object' ? (payload.data as RawRecord) : payload;
  const metricsRaw = (data?.metrics as RawRecord | undefined) ?? {};
  const metrics: Record<string, UsageMetric> = {};

  Object.entries(metricsRaw).forEach(([key, value]) => {
    metrics[key] = normalizeUsageMetric(value as RawRecord);
  });

  const periodRaw = data?.period && typeof data.period === 'object' ? (data.period as RawRecord) : {};

  return {
    period: {
      start: readString(periodRaw.start),
      end: readString(periodRaw.end),
      timezone: readString(periodRaw.timezone),
    },
    metrics,
  };
}

function normalizeUsageRecord(record: RawRecord): UsageRecord {
  return {
    id: readString(record.id) ?? crypto.randomUUID(),
    recordedAt: readString(record.recorded_at ?? record.recordedAt) ?? '',
    usageType: readString(record.usage_type ?? record.usageType) ?? 'unknown',
    endpointId: readString(record.endpoint_id ?? record.endpointId),
    endpointPath: readString(record.endpoint_path ?? record.endpointPath),
    quantity: readNumber(record.quantity),
    creditsConsumed: readNumber(record.credits_consumed ?? record.creditsConsumed),
    costAmount: readNumber(record.cost_amount ?? record.costAmount),
    currency: readString(record.currency),
    metadata: record.metadata && typeof record.metadata === 'object' ? (record.metadata as Record<string, unknown>) : null,
  };
}

function normalizeUsageRecords(payload: RawRecord | undefined): UsageRecordsResponse {
  const dataArray =
    (payload?.data as RawRecord[] | undefined) ??
    (Array.isArray(payload) ? (payload as RawRecord[]) : undefined) ??
    [];

  const metaRaw = (payload?.meta as RawRecord | undefined) ?? undefined;

  return {
    data: dataArray.map((item) => normalizeUsageRecord(item)),
    meta: metaRaw
      ? {
          currentPage: metaRaw.current_page as number | undefined ?? (metaRaw.currentPage as number | undefined),
          perPage: metaRaw.per_page as number | undefined ?? (metaRaw.perPage as number | undefined),
          total: metaRaw.total as number | undefined,
          lastPage: metaRaw.last_page as number | undefined ?? (metaRaw.lastPage as number | undefined),
        }
      : undefined,
  };
}

function normalizeInvoice(record: RawRecord): InvoiceRecord {
  return {
    id: readString(record.id) ?? crypto.randomUUID(),
    amountDue: readNumber(record.amount_due ?? record.amountDue) ?? 0,
    currency: readString(record.currency) ?? 'USD',
    status: readString(record.status) ?? 'unknown',
    issuedAt: readString(record.issued_at ?? record.issuedAt),
    paidAt: readString(record.paid_at ?? record.paidAt),
    hostedInvoiceUrl: readString(record.hosted_invoice_url ?? record.hostedInvoiceUrl),
    invoicePdf: readString(record.invoice_pdf ?? record.invoicePdf),
  };
}

function normalizeInvoices(payload: RawRecord | undefined): InvoicesResponse {
  const dataArray =
    (payload?.data as RawRecord[] | undefined) ??
    (Array.isArray(payload) ? (payload as RawRecord[]) : undefined) ??
    [];

  return {
    data: dataArray.map((item) => normalizeInvoice(item)),
  };
}

function normalizePaymentMethod(payload: RawRecord | undefined): PaymentMethodSummary {
  if (!payload) {
    return { status: 'error', message: 'Payment method unavailable' };
  }

  const status = readString(payload.status) as PaymentMethodSummary['status'] | null;

  if (!status) {
    return { status: 'error', message: 'Unknown payment method status' };
  }

  return {
    status,
    brand: readString(payload.brand) ?? undefined,
    last4: readString(payload.last4) ?? undefined,
    expMonth: readNumber(payload.exp_month ?? payload.expMonth) ?? undefined,
    expYear: readNumber(payload.exp_year ?? payload.expYear) ?? undefined,
    funding: readString(payload.funding) ?? undefined,
    message: readString(payload.message) ?? undefined,
  };
}

function normalizePayLink(payload: RawRecord | undefined): PayLinkResponse {
  const link = payload?.pay_link && typeof payload.pay_link === 'object'
    ? (payload.pay_link as RawRecord)
    : payload;

  return {
    url: readString(link?.url) ?? '',
    expiresAt: readString(link?.expires_at ?? link?.expiresAt),
    isPlaceholder: readBoolean(link?.is_placeholder ?? link?.isPlaceholder, false),
    stripeSubscriptionId: readString(link?.stripe_subscription_id ?? link?.stripeSubscriptionId),
  };
}

export interface UsageRecordsQuery {
  customerId: string;
  metric?: string;
  endpointId?: string;
  startDate?: string;
  endDate?: string;
  sort?: string;
  page?: number;
  perPage?: number;
}

export interface ChangePlanRequest {
  planId: string;
  billingCycleId?: string;
  prorate?: boolean;
  workspaceName?: string;
}

export interface SelectSubscriptionRequest {
  planId: string;
}

class ProviderCustomerPreviewService {
  async getSubscription(customerId: string): Promise<ProviderSubscriptionPreview> {
    const response = await apiClient.get<RawRecord>(`/api/v1/billing/customers/${customerId}/subscription`);
    const payload = (response.data as RawRecord | undefined) ?? undefined;
    const metaRaw = payload?.meta && typeof payload.meta === 'object'
      ? (payload.meta as RawRecord)
      : {};
    const planOptionsRaw = Array.isArray(metaRaw.plan_options ?? metaRaw.planOptions)
      ? (metaRaw.plan_options ?? metaRaw.planOptions) as unknown[]
      : [];
    const planOptions = planOptionsRaw
      .map((entry) => {
        if (!entry || typeof entry !== 'object') {
          return null;
        }
        return normalizeSubscriptionPlanOptionResource(entry as Record<string, unknown> as {
          id: string;
          attributes?: Record<string, unknown>;
        });
      })
      .filter((entry): entry is SubscriptionPlanOption => Boolean(entry));

    const meta: ProviderSubscriptionPreviewMeta = {
      hasSubscription: readBoolean(metaRaw.has_subscription ?? metaRaw.hasSubscription, false),
      hasUsableSubscription: readBoolean(
        metaRaw.has_usable_subscription ?? metaRaw.hasUsableSubscription,
        false,
      ),
      canSelectSubscription: readBoolean(
        metaRaw.can_select_subscription ?? metaRaw.canSelectSubscription,
        false,
      ),
      ...(planOptions.length > 0 ? { planOptions } : {}),
    };

    const resource =
      (payload?.data as RawRecord | undefined) ??
      (payload?.subscription as RawRecord | undefined) ??
      (
        payload && (payload.id || payload.status || payload.plan || payload.attributes)
          ? payload
          : undefined
      );

    if (!resource) {
      return {
        subscription: null,
        meta,
      };
    }

    return {
      subscription: normalizeSubscription(resource),
      meta,
    };
  }

  async getUsageSummary(customerId: string): Promise<UsageSummary> {
    const response = await apiClient.get<RawRecord>(`/api/v1/billing/usage/summary?customer_id=${customerId}`);
    return normalizeUsageSummary(response.data as RawRecord | undefined);
  }

  async getUsageRecords(params: UsageRecordsQuery): Promise<UsageRecordsResponse> {
    const searchParams = new URLSearchParams();
    if (params.customerId) searchParams.set('customer_id', params.customerId);
    if (params.metric) searchParams.set('metric', params.metric);
    if (params.endpointId) searchParams.set('endpoint_id', params.endpointId);
    if (params.startDate) searchParams.set('start_date', params.startDate);
    if (params.endDate) searchParams.set('end_date', params.endDate);
    if (params.sort) searchParams.set('sort', params.sort);
    if (typeof params.page === 'number') searchParams.set('page', String(params.page));
    if (typeof params.perPage === 'number') searchParams.set('per_page', String(params.perPage));

    const query = searchParams.toString();
    const response = await apiClient.get<RawRecord>(`/api/v1/billing/usage-records${query ? `?${query}` : ''}`);
    return normalizeUsageRecords(response.data as RawRecord | undefined);
  }

  async getInvoices(customerId: string, limit = 3): Promise<InvoicesResponse> {
    const response = await apiClient.get<RawRecord>(
      `/api/v1/billing/customers/${customerId}/invoices?limit=${limit}`,
    );
    return normalizeInvoices(response.data as RawRecord | undefined);
  }

  async getPaymentMethod(customerId: string): Promise<PaymentMethodSummary> {
    const response = await apiClient.get<RawRecord>(
      `/api/v1/billing/customers/${customerId}/payment-method`,
    );
    return normalizePaymentMethod(response.data as RawRecord | undefined);
  }

  async changePlan(
    subscriptionId: string,
    request: ChangePlanRequest,
  ): Promise<ProviderSubscriptionSummary> {
    const body: Record<string, unknown> = { plan_id: request.planId };
    if (request.billingCycleId) {
      body.billing_cycle_id = request.billingCycleId;
    }
    if (typeof request.prorate === 'boolean') {
      body.prorate = request.prorate;
    }
    if (typeof request.workspaceName === 'string' && request.workspaceName.trim().length > 0) {
      body.workspace_name = request.workspaceName.trim();
    }
    const response = await apiClient.post<RawRecord>(
      `/api/v1/billing/subscriptions/${subscriptionId}/change-plan`,
      body,
    );
    const payload = (response.data as RawRecord | undefined) ?? undefined;
    const resource = (payload?.data as RawRecord | undefined) ?? payload;
    return normalizeSubscription(resource);
  }

  async selectSubscription(
    customerId: string,
    request: SelectSubscriptionRequest,
  ): Promise<ProviderSubscriptionSummary | null> {
    const body: Record<string, unknown> = {
      customer_id: customerId,
      subscription_plan_id: request.planId,
      status: 'active',
    };

    const response = await apiClient.post<RawRecord>(
      `/api/v1/billing/subscriptions`,
      body,
    );
    const payload = (response.data as RawRecord | undefined) ?? undefined;
    const resource =
      (payload?.data as RawRecord | undefined) ??
      (payload?.subscription as RawRecord | undefined) ??
      (
        payload && (payload.id || payload.status || payload.plan || payload.attributes)
          ? payload
          : undefined
      );

    if (!resource) {
      return null;
    }

    return normalizeSubscription(resource);
  }

  async issuePayLink(subscriptionId: string): Promise<PayLinkResponse> {
    const response = await apiClient.post<RawRecord>(
      `/api/v1/billing/subscriptions/${subscriptionId}/pay-link`,
    );
    return normalizePayLink(response.data as RawRecord | undefined);
  }
}

export const providerCustomerPreviewService = new ProviderCustomerPreviewService();
